import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes, isSuperAdminUser } from '../../Utils/Permissions';
import { APPRoutes } from '../../Utils/Route';
import { getAllOrganizationNames } from '../../Services/OrganizationService';
import { getAllClientNames } from '../../Services/ClientService';
import { saveLocation, getLocationById } from '../../Services/LocationService';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';
import AddressSelectorFields from '../../Components/Common/AddressSelectorFields';
import CustomPhoneInput from '../../Components/Common/CustomPhoneInput';
import DotLottie from '../../Components/Common/DotLottie';

const STATUS_OPTIONS = [
  { id: 1, label: 'Active' },
  { id: 2, label: 'Inactive' },
];

const emptyForm = {
  id: null,
  account_id: '',
  organization_id: '',
  address_id: null,
  status_id: 1,
  name: '',
  phone: '',
  fax: '',
  type: 'create',
  address: {
    street: '',
    street2: '',
    city: '',
    state: '',
    country: '',
    zipcode: '',
  },
};

const validationSchema = Yup.object({
  name: Yup.string().trim().required('Location name is required'),
  organization_id: Yup.string().trim().required('Organization is required'),
  account_id: Yup.string().trim().required('Client is required'),
  status_id: Yup.number().required('Status is required'),
  address: Yup.object({
    street: Yup.string().trim().required('Street Address is required'),
    street2: Yup.string().trim().nullable(),
    city: Yup.string().trim().required('City is required'),
    state: Yup.string().trim().required('State is required'),
    country: Yup.string().trim().required('Country is required'),
    zipcode: Yup.string().trim().required('ZIP Code is required'),
  }),
});

function mapApiToForm(locationData, isSuperAdmin, accountId) {
  return {
    id: locationData?.id ?? null,
    account_id: isSuperAdmin
      ? locationData?.account_id ?? locationData?.AccountId ?? locationData?.accountId ?? ''
      : (accountId || (locationData?.account_id ?? locationData?.AccountId ?? locationData?.accountId ?? '')),
    organization_id:
      locationData?.organization_id ?? locationData?.OrganizationId ?? locationData?.organizationId ?? '',
    address_id: locationData?.address_id ?? locationData?.AddressId ?? null,
    status_id: locationData?.status_id ?? locationData?.StatusId ?? 1,
    name: locationData?.name ?? locationData?.Name ?? '',
    phone: locationData?.phone ?? locationData?.Phone ?? '',
    fax: locationData?.fax ?? locationData?.Fax ?? '',
    type: locationData?.type ?? (locationData?.id ? 'update' : 'create'),
    address: {
      street:
        locationData?.address?.street ??
        locationData?.address?.Street ??
        locationData?.street ??
        locationData?.Street ??
        '',
      street2:
        locationData?.address?.street2 ??
        locationData?.address?.Street2 ??
        locationData?.street2 ??
        locationData?.Street2 ??
        '',
      city:
        locationData?.address?.city ??
        locationData?.address?.City ??
        locationData?.city ??
        locationData?.City ??
        '',
      state:
        locationData?.address?.state ??
        locationData?.address?.State ??
        locationData?.state ??
        locationData?.State ??
        '',
      country:
        locationData?.address?.country ??
        locationData?.address?.Country ??
        locationData?.country ??
        locationData?.Country ??
        '',
      zipcode:
        locationData?.address?.zipcode ??
        locationData?.address?.Zipcode ??
        locationData?.zipcode ??
        locationData?.Zipcode ??
        '',
    },
  };
}

function buildPayload(values, isSuperAdmin, accountId) {
  return {
    id: values.id || null,
    account_id: isSuperAdmin ? values.account_id || null : accountId || values.account_id || null,
    organization_id: values.organization_id || null,
    address_id: values.address_id || null,
    status_id: values.status_id,
    name: values.name,
    phone: values.phone || null,
    fax: values.fax || null,
    type: values.id ? 'update' : 'create',
    address: {
      street: values.address.street,
      street2: values.address.street2,
      city: values.address.city,
      state: values.address.state,
      country: values.address.country,
      zipcode: values.address.zipcode,
    },
  };
}

export default function LocationForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { token, permissions, decodedToken } = useAuth();
  const isSuperAdmin = isSuperAdminUser(decodedToken);
  const accountId = decodedToken?.account_id;

  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const canCreateLocation = permissionHas(userPermissionCodes, PermissionCodes.LOCATIONS_CREATE, decodedToken);
  const canEditLocation = permissionHas(userPermissionCodes, PermissionCodes.LOCATIONS_EDIT, decodedToken);
  const canSave = isEdit ? canEditLocation : canCreateLocation;

  const [loading, setLoading] = useState(isEdit);
  const [organizations, setOrganizations] = useState([]);
  const [clients, setClients] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [clientLoading, setClientLoading] = useState(false);

  const initialValues = useMemo(() => ({
    ...emptyForm,
    account_id: isSuperAdmin ? '' : accountId || '',
  }), [isSuperAdmin, accountId]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: false,
    validationSchema,
    onSubmit: async (values) => {
      try {
        const payload = buildPayload(values, isSuperAdmin, accountId);
        const response = await saveLocation(payload, token);
        if (response?.is_success) {
          toast.success(response?.message || 'Location saved successfully');
          const locationId = response?.data?.Id || response?.data || id;
          navigate(APPRoutes.LOCATIONS_VIEW.replace(':id', locationId));
        } else if (response?.is_success === false && response?.status_code === 500) {
          toast.error('Internal Server Error. Please Contact Support Team.');
        } else {
          toast.error(response?.message || 'Failed to save location');
        }
      } catch (error) {
        console.error(error);
        toast.error(error.message || 'Failed to save location');
      }
    },
  });

  const loadLocation = async () => {
    if (!isEdit) return;

    try {
      setLoading(true);
      const response = await getLocationById(id, token);
      if (response?.is_success) {
        const locationData = response?.data ?? response;
        formik.setValues(mapApiToForm(locationData, isSuperAdmin, accountId));
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        toast.error(response?.message || 'Failed to load location');
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Failed to load location');
    } finally {
      setLoading(false);
    }
  };

  const normalizeArray = (value) => (Array.isArray(value) ? value : []);

  const loadOrganizations = async () => {
    try {
      setOrgLoading(true);
      const response = await getAllOrganizationNames(token);
      if (response?.is_success) {
        const data = normalizeArray(response?.data?.Organizations ?? response?.data ?? response);
        setOrganizations(data);
      } else {
        setOrganizations([]);
      }
    } catch (error) {
      console.error('Failed to load organizations:', error);
      setOrganizations([]);
    } finally {
      setOrgLoading(false);
    }
  };

  const loadClients = async () => {
    if (!isSuperAdmin) {
      setClients([]);
      return;
    }

    try {
      setClientLoading(true);
      const response = await getAllClientNames(token);
      if (response?.is_success) {
        const data = normalizeArray(response?.data ?? response);
        setClients(data);
      } else {
        setClients([]);
      }
    } catch (error) {
      console.error('Failed to load clients:', error);
      setClients([]);
    } finally {
      setClientLoading(false);
    }
  };

  React.useEffect(() => {
    if (!token) return;
    loadOrganizations();
    loadClients();
    if (isEdit) {
      loadLocation();
    }
  }, [id, isEdit, token]);

  const renderAsterisk = () => <span className="text-danger">*</span>;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <DotLottie />
      </div>
    );
  }

  return (
    (!isEdit && !canCreateLocation) ? (
      <div className="container-fluid py-4">
        <div className="p-4 bg-white rounded-4 shadow-sm">
          <h5 className="mb-2">Permission denied</h5>
          <p className="text-muted">You do not have permission to create locations.</p>
          <button type="button" className="btn btn-outline-primary" onClick={() => navigate(APPRoutes.LOCATIONS)}>
            Back to Locations
          </button>
        </div>
      </div>
    ) : ( 
    <div className="container-fluid p-0">
      <div className="rounded-4 bg-white p-2 p-md-4 shadow-lg border-0">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-3">
            <button
              type="button"
              className="btn btn-light border rounded-circle shadow-sm"
              onClick={() => navigate(-1)}
              style={{ width: 40, height: 40 }}
            >
              &larr;
            </button>
            <div>
              <h3 className="fw-bold mb-0 hc-text-primary">
                {isEdit ? 'Edit Location' : 'Create Location'}
              </h3>
              <small className="text-muted">
                Manage location details, assignment and address information
              </small>
            </div>
          </div>
        </div>

        <form onSubmit={formik.handleSubmit}>
          <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
            <h6 className="fw-bold hc-text-primary mb-3">Location Details</h6>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  Location Name {renderAsterisk()}
                </label>
                <input
                  name="name"
                  placeholder="Main Clinic"
                  className={`form-control rounded-3 shadow-sm ${formik.touched.name && formik.errors.name ? 'is-invalid' : ''}`}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <div className="invalid-feedback">{formik.errors.name}</div>
              </div>

              <div className="col-md-6">
                <SearchableDropdown
                  label="Status"
                  placeholder="Select Status"
                  options={STATUS_OPTIONS.map((item) => ({ value: item.id, label: item.label }))}
                  value={formik.values.status_id}
                  onChange={(value) => formik.setFieldValue('status_id', value !== '' ? Number(value) : '')}
                  showSearch={false}
                  required
                  error={formik.touched.status_id && formik.errors.status_id ? formik.errors.status_id : ''}
                />
              </div>
            </div>

            <div className="row g-3 mt-1">
              <div className="col-md-6">
                <SearchableDropdown
                  label="Organization"
                  placeholder="Select organization"
                  options={(Array.isArray(organizations) ? organizations : []).map((org) => ({
                    value: org.id ?? org.OrganizationId ?? org.organization_id ?? org.Id ?? org.value ?? '',
                    label:
                      org.group_office_name ??
                      org.OrganizationName ??
                      org.organization_name ??
                      org.name ??
                      org.label ??
                      'Unknown Organization',
                  }))}
                  value={formik.values.organization_id}
                  onChange={(value) => formik.setFieldValue('organization_id', value || '')}
                  disabled={orgLoading}
                  loading={orgLoading}
                  required
                  emptyMessage="No organizations found"
                />
                <div className="invalid-feedback d-block">{formik.touched.organization_id && formik.errors.organization_id ? formik.errors.organization_id : ''}</div>
              </div>

              {isSuperAdmin && (
                <div className="col-md-6">
                  <SearchableDropdown
                    label="Client"
                    placeholder="Select client"
                    options={(Array.isArray(clients) ? clients : []).map((client) => ({
                      value: client.Id ?? client.id ?? client.value ?? '',
                      label:
                        client.OrganizationName ??
                        client.organization_name ??
                        client.name ??
                        client.label ??
                        'Unknown Client',
                    }))}
                    value={formik.values.account_id}
                    onChange={(value) => formik.setFieldValue('account_id', value || '')}
                    disabled={clientLoading}
                    loading={clientLoading}
                    required
                    emptyMessage="No clients found"
                  />
                  <div className="invalid-feedback d-block">{formik.touched.account_id && formik.errors.account_id ? formik.errors.account_id : ''}</div>
                </div>
              )}
            </div>

            <div className="row g-3 mt-1">
              <div className="col-md-6">
                <label className="form-label fw-semibold">Phone</label>
                {/* <input
                  name="phone"
                  placeholder="+1 (555) 123-4567"
                  className="form-control rounded-3 shadow-sm"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                /> */}

                <CustomPhoneInput
                formik={formik}
                name="phone"
                placeholder="+1 (555) 123-4567"
                defaultCountry="us"
                />
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">Fax</label>
                <input
                  name="fax"
                  placeholder="(555) 123-4568"
                  className="form-control rounded-3 shadow-sm"
                  value={formik.values.fax}
                  onChange={formik.handleChange}
                />
              </div>
            </div>
          </div>

          <div className="p-4 rounded-4 border bg-light-subtle">
            <h6 className="fw-bold hc-text-primary mb-3">Address</h6>
            <AddressSelectorFields
              title="Location Address"
              fieldPrefix="address"
              values={formik.values.address}
              errors={formik.errors.address || {}}
              touched={formik.touched.address || {}}
              onFieldChange={(fieldName, value) => formik.setFieldValue(fieldName, value)}
              renderAsterisk={renderAsterisk}
            />
          </div>

          <div className="text-end mt-4">
            <button
              type="submit"
              className="btn hc-bg-primary text-white px-5 py-2 rounded-3 fw-semibold shadow"
              disabled={formik.isSubmitting || !canSave}
            >
              {formik.isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Saving...
                </>
              ) : (
                (canSave ? 'Save' : 'You do not have permission to save')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
    )
  );
}

